/***
 * TypeScript Entry Point
 * IMPORTANT: This is a global-modifying module.
 * @global
 * @see https://www.typescriptlang.org/docs/handbook/declaration-files/templates/global-modifying-module-d-ts.html
 * @author Arthur Moore <ArthurMO@mohela.com>
 * @copyright MOHELA 2025
 ***/


import $ from "@/common/jquery.js";

import { hackExportFnsToWindow, InitializeAccordions } from '@/common/common.js';
import { logout } from "@/common/logout.js";
import { InitializeGridOptionCheckbox } from "@/common/showAllCheckbox.js";
import { InitializeNavBar, InitializeSearch } from "@/common/site.js";
import { aspControlValidationInit, customValidationInit } from '@/common/validation/init.js';
import { clearFormData, handleNoValidateButton, handleNoValidateConfirmButton, hide_Error_Prompts, isPageValid, processADRS2, processESign, processESignCustom, validateLNEFStep2, validateSendMessage, validateUpLoadAttachments, validateUploadFile } from '@/common/validation/utils.js';

import { InitializeBankAccountControl } from "@/controls/BankAccount.ascx.js";
import { IntializeCommonCalendarControl } from "@/controls/CommonCalendar.ascx.js";
import { InitializePasswordCheckerControl } from "@/controls/PasswordChecker.ascx.js";
import { InitializeSpecialMessagesControl } from "@/controls/SpecialMessages.ascx.js";

import { InitializeScheduleCallPage } from "@/pages/DL/common/ScheduleCall.aspx.js";
import { InitializeContactUsPage } from "@/pages/DL/common/contactUs.aspx.js";
import { InitializeAdditionalResourcesPage } from "@/pages/DL/resourceCenter/AdditionalResources.aspx.js";
import { InitializeLoginPage } from "@/pages/DL/secure/account/loginStep1.aspx.js";
import { handleMultipleDueDates } from "@/pages/DL/secure/borrower/AccountSummary.aspx.js";
import { InitializeFindFormListPage } from "@/pages/DL/secure/borrower/FindFormList.aspx.js";
import { InitializeGFBStep2Page } from "@/pages/DL/secure/borrower/FormWizard/GFB/GFBStep2.aspx.js";
import { InitializeForm4506CStep2Page, validateForm4506C } from "@/pages/DL/secure/borrower/FormWizard/ISA/Form4506C/Form4506CStep2.aspx.js";
import { InitializeForm4506TStep2Page, validateForm4506T } from "@/pages/DL/secure/borrower/FormWizard/ISA/Form4506T/Form4506TStep2.aspx.js";
import { InitializeIncomeChangePage } from "@/pages/DL/secure/borrower/ISA/IncomeChange/IncomeChange.aspx.js";
import { InitializeIncomeChangeDocumentationPage } from "@/pages/DL/secure/borrower/ISA/IncomeChange/IncomeChangeDocumentation.aspx.js";
import { InitializeLenderSelectionPage } from "@/pages/DL/secure/borrower/ISA/LenderSelection.aspx.js";
import { InitializeProgramSelectionPage } from "@/pages/DL/secure/borrower/PL/ProgramSelection.aspx.js";
import { InitializeUploadFilePage } from "@/pages/DL/secure/borrower/UploadFile.aspx.js";
import { InitializeEarlyPayoffCalculatorPage } from "@/pages/DL/secure/borrower/calculator/earlyPayoffCalculator.aspx.js";
import { InitializeFindFormPage } from "@/pages/DL/secure/borrower/findForm.aspx.js";
import { InitializeOLPPaymentAccountsPage } from "@/pages/DL/secure/borrower/onlinePayment/OLPPaymentAccounts.aspx.js";
import { InitializeOLPPaymentSchedulingPage } from "@/pages/DL/secure/borrower/onlinePayment/OLPPaymentScheduling.aspx.js";
import { InitializeOLPPaymentSchedulingReviewPage } from "@/pages/DL/secure/borrower/onlinePayment/OLPPaymentSchedulingReview.aspx.js";
import { InitializeProfilePage } from "@/pages/DL/secure/borrower/profile.aspx.js";
import { InitializeSecureBorrowerRepaymentOptionsIDRstep3Page } from "@/pages/DL/secure/borrower/repaymentOptions/IDR/step3.aspx.js";
import { InitializeGPLMessagesDefaultPage } from "@/pages/DL/secure/messages/GPLMessagesDefault.aspx.js";
import { InitializeGPLRfaInfoGetPage } from "@/pages/DL/secure/messages/GPLRfaInfoGet.aspx.js";
import { InitializeDefaultPage } from "@/pages/default.aspx.js";
import { InitializeFAQPage } from "@/pages/DL/FAQs/FAQPage.aspx.js";
import { InitializeDLFaQsFAQsPage } from "@/pages/DL/FAQs/FAQs.aspx.js";
import { InitializeAddLoanStep1Page } from "@/pages/DL/secure/borrower/AutoDebit/enrollment/AddLoanStep1.aspx.js";
import { InitializeAddLoanStep2Page } from "@/pages/DL/secure/borrower/AutoDebit/enrollment/AddLoanStep2.aspx.js";
import { InitializeAddLoanStep3Page } from "@/pages/DL/secure/borrower/AutoDebit/enrollment/AddLoanStep3.aspx.js";
import { InitializeNewAccountInformationPage } from "@/pages/DL/secure/account/new/newAccountInformation.aspx.js";
import { InitializeNewAccountPaperlessPage } from "@/pages/DL/secure/account/new/newAccountPaperless.aspx.js";
import { InitializeNewAccountSecurityPage } from "@/pages/DL/secure/account/new/newAccountSecurity.aspx.js";
import { InitializeDLSecureBorrowerAutoDebitEnrollment2completePage } from "@/pages/DL/secure/borrower/AutoDebit/enrollment/2complete.aspx.js";
import { InitializeSecureBorrowerAutoDebitEnrollment3reviewPage } from "@/pages/DL/secure/borrower/AutoDebit/enrollment/3Review.aspx.js";
import { InitializeDLSecureBorrowerAutoDebitPaidAheadPage } from "@/pages/DL/secure/borrower/AutoDebit/PaidAhead.aspx.js";
import { InitializeDLSecureBorrowerAutoDebitCancelPaymentPage } from "@/pages/DL/secure/borrower/AutoDebit/cancelPayment.aspx.js";
import { InitializeDLSecureBorrowerAutoDebitChangeAmountPage } from "@/pages/DL/secure/borrower/AutoDebit/changeAmount.aspx.js";
import { InitializeDLSecureBorrowerAutoDebitSuspendPaidAheadPage } from "@/pages/DL/secure/borrower/AutoDebit/suspendPaidAhead.aspx.js";
import { InitializeDLSecureBorrowerAutoDebitSuspendPaymentPage } from "@/pages/DL/secure/borrower/AutoDebit/suspendPayment.aspx.js";
import { InitializeIncomeChangeRequestPage } from "@/pages/DL/secure/borrower/ISA/IncomeChange/IncomeChangeRequest.aspx.js";
import { InitializeIncomeChangeSecondOccupationPage } from "@/pages/DL/secure/borrower/ISA/IncomeChange/IncomeChangeSecondOccupation.aspx.js";
import { CurrentPaymentInstructionsControl } from "@/controls/CurrentPaymentInstructions.ascx.js";
import { InitializePaymentInstructionsPage } from "@/pages/DL/secure/borrower/PaymentInstructions/PaymentInstructions.aspx.js";
import { InitializePaymentInstructionsDefaultPage } from "@/pages/DL/secure/borrower/PaymentInstructions/PaymentInstructionsDefault.aspx.js";
import { InitializeRTSReviewPage } from "@/pages/DL/secure/borrower/FormWizard/RTS_SOFI/RTSReview.aspx.js";
import { InitializeRTSStep0Page } from "@/pages/DL/secure/borrower/FormWizard/RTS_SOFI/RTSStep0.aspx.js";
import { InitializeRTSStep1Page } from "@/pages/DL/secure/borrower/FormWizard/RTS_SOFI/RTSStep1.aspx.js";
import { InitializeRTSStep2Page } from "@/pages/DL/secure/borrower/FormWizard/RTS_SOFI/RTSStep2.aspx.js";
import { InitializeRTSStep3Page } from "@/pages/DL/secure/borrower/FormWizard/RTS_SOFI/RTSStep3.aspx.js";
import { InitializeRTSStep3chPage } from "@/pages/DL/secure/borrower/FormWizard/RTS_SOFI/RTSStep3ch.aspx.js";
import { InitializeRTSStep4Page } from "@/pages/DL/secure/borrower/FormWizard/RTS_SOFI/RTSStep4.aspx.js";
import { InitializeRTSStep5Page } from "@/pages/DL/secure/borrower/FormWizard/RTS_SOFI/RTSStep5.aspx.js";
import { InitializeSecureBorrowerRepaymentOptionsRepaymentCalculatorPage } from "@/pages/DL/secure/borrower/repaymentOptions/RepaymentCalculator.aspx.js";
import { InitializeSecureBorrowerRepaymentOptionsIDRRepaymentRenewalPage } from "@/pages/DL/secure/borrower/repaymentOptions/RepaymentRenewal.aspx.js";
import { InitializeSecureBorrowerSurveyEmailSurveyPage } from "@/pages/DL/secure/borrower/survey/EmailSurvey.aspx.js";
import { InitializeSecureBorrowerSurveyGeneralSurveyPage } from "@/pages/DL/secure/borrower/survey/generalSurvey.aspx.js";
import { InitializeSecureBorrowerSurveyLogoutSurveyPage } from "@/pages/DL/secure/borrower/survey/logoutSurvey.aspx.js";

/**
 * Page specific JavaScript.
 * Call the initialization function on the appropriate page.
 * @example
 * ```
 * <script type="text/javascript">
 *   document.addEventListener('ClientLoaded', function (event) {
 *     PageInit.InitializeContactUsPage();
 *   });
 * </script>
 * ```
 */
const initializationFunctions = {
  InitializeSpecialMessagesControl,
  IntializeCommonCalendarControl,
  InitializeBankAccountControl,
  InitializePasswordCheckerControl,
  InitializeLoginPage,
  InitializeContactUsPage,
  InitializeForm4506CStep2Page,
  InitializeForm4506TStep2Page,
  InitializeDefaultPage,
  InitializeScheduleCallPage,
  InitializeOLPPaymentSchedulingPage,
  InitializeOLPPaymentAccountsPage,
  InitializeIncomeChangePage,
  InitializeGPLRfaInfoGetPage,
  InitializeAdditionalResourcesPage,
  InitializeOLPPaymentSchedulingReviewPage,
  InitializeEarlyPayoffCalculatorPage,
  InitializeGFBStep2Page,
  InitializeProfilePage,
  InitializeGPLMessagesDefaultPage,
  InitializeFindFormListPage,
  InitializeUploadFilePage,
  InitializeFindFormPage,
  InitializeLenderSelectionPage,
  InitializeProgramSelectionPage,
  InitializeSecureBorrowerRepaymentOptionsIDRstep3Page,
  InitializeIncomeChangeDocumentationPage,
  InitializeFAQPage,
  InitializeDLFaQsFAQsPage,
  InitializeNewAccountInformationPage,
  InitializeNewAccountPaperlessPage,
  InitializeNewAccountSecurityPage,
  InitializeAddLoanStep1Page,
  InitializeAddLoanStep2Page,
  InitializeAddLoanStep3Page,
  InitializeDLSecureBorrowerAutoDebitEnrollment2completePage,
  InitializeSecureBorrowerAutoDebitEnrollment3reviewPage,
  InitializeDLSecureBorrowerAutoDebitCancelPaymentPage,
  InitializeDLSecureBorrowerAutoDebitSuspendPaidAheadPage,
  InitializeDLSecureBorrowerAutoDebitSuspendPaymentPage,
  InitializeDLSecureBorrowerAutoDebitPaidAheadPage,
  InitializeDLSecureBorrowerAutoDebitChangeAmountPage,
  InitializeIncomeChangeSecondOccupationPage,
  InitializeIncomeChangeRequestPage,
  InitializePaymentInstructionsPage,
  InitializePaymentInstructionsDefaultPage,
  InitializeRTSReviewPage,
  InitializeRTSStep0Page,
  InitializeRTSStep1Page,
  InitializeRTSStep2Page,
  InitializeRTSStep3Page,
  InitializeRTSStep3chPage,
  InitializeRTSStep4Page,
  InitializeRTSStep5Page,
  InitializeSecureBorrowerRepaymentOptionsRepaymentCalculatorPage,
  InitializeSecureBorrowerRepaymentOptionsIDRRepaymentRenewalPage,
  InitializeSecureBorrowerSurveyLogoutSurveyPage,
  InitializeSecureBorrowerSurveyGeneralSurveyPage,
  InitializeSecureBorrowerSurveyEmailSurveyPage,
  InitializeGPLSendSecureEmailPage: () => customValidationInit(false),
};

declare interface ControlManager {
  CurrentPaymentInstructionsControl: CurrentPaymentInstructionsControl | null
}

/**
 * Global types.
 * Supports things which we expect to see / have available everywhere.
 */
declare global {
  interface Window {
    $: JQueryStatic

    logout: typeof logout
    PageInit: typeof initializationFunctions,

    // Validation (embedded in page, or called using strings)
    aspControlValidationInit: typeof aspControlValidationInit,
    customValidationInit: typeof customValidationInit,
    validateLNEFStep2: typeof validateLNEFStep2,
    validateSendMessage: typeof validateSendMessage,
    validateUploadFile: typeof validateUploadFile,
    validateUpLoadAttachments: typeof validateUpLoadAttachments,
    handleNoValidateButton: typeof handleNoValidateButton,
    handleNoValidateConfirmButton: typeof handleNoValidateConfirmButton,
    processESign: typeof processESign,
    processESignCustom: typeof processESignCustom,
    processADRS2: typeof processADRS2,
    hide_Error_Prompts: typeof hide_Error_Prompts,
    isPageValid: typeof isPageValid,
    clearFormData: typeof clearFormData,

    // Page specific functions
    handleMultipleDueDates: typeof handleMultipleDueDates
    validateForm4506C: typeof validateForm4506C
    validateForm4506T: typeof validateForm4506T

    controls: ControlManager
  }
}

// Set the global jQuery instance
window.$ = $;

window.logout = logout;
window.PageInit = initializationFunctions;

// Validation (embedded in page, or called using strings)
window.aspControlValidationInit = aspControlValidationInit;
window.customValidationInit = customValidationInit;
window.validateLNEFStep2 = validateLNEFStep2;
window.validateSendMessage = validateSendMessage;
window.validateUploadFile = validateUploadFile;
window.validateUpLoadAttachments = validateUpLoadAttachments;
window.handleNoValidateButton = handleNoValidateButton;
window.handleNoValidateConfirmButton = handleNoValidateConfirmButton;
window.processESign = processESign;
window.processESignCustom = processESignCustom;
window.processADRS2 = processADRS2;
window.hide_Error_Prompts = hide_Error_Prompts;
window.isPageValid = isPageValid;
window.clearFormData = clearFormData;

// Page specific functions
window.handleMultipleDueDates = handleMultipleDueDates;
window.validateForm4506C = validateForm4506C;
window.validateForm4506T = validateForm4506T;

/**
 * @summary Run all the global and per page JavaScript.
 * @description Allows for inline JavaScript while still having JQuery available!
 *              Also allows for individual TypeScript functions to be accessed by the inline JavaScript.
 *              Per page JS is triggered by an inline script tag on the aspx page.
 */
export function run() {
  const loadedEvent = new Event("ClientLoaded", { bubbles: true, cancelable: false });
  //JQuery recommended version of `$(document).ready`
  $(function () {
    //alert("TypeScript JQuery Testing in progress");
    window.controls = {
      CurrentPaymentInstructionsControl: new CurrentPaymentInstructionsControl(),
    };

    hackExportFnsToWindow();
    InitializeSearch();
    InitializeNavBar();
    InitializeAccordions();
    InitializeGridOptionCheckbox();

    document.dispatchEvent(loadedEvent);
  });
}
